/** @jsxImportSource @emotion/react */
import { DeniedBadge, isDeniedAdjudication } from 'components/lab/billingEraReports/AdjustmentCodes'
import { TooltipWrapper } from 'components/atoms/TooltipWrapper'
import { confirm } from 'components/molecules/Confirmation'
import React, { useState } from 'react'
import { ChargeAccountLineApplication, ChargeAccountServiceLine, ChargeAccountTransaction } from 'store/types'
import { ApplyPaymentRow, ChargePayerChain } from './ApplyPaymentRow'
import { reversesWholePayment, TransactionRow } from './TransactionRow'
import { isFullyReversed } from './transactionReversal'
import { PatientApplicationRow } from './PatientApplicationRow'
import { lineActivity } from './lineActivity'
import { useLabPermission } from 'hooks/useLabPermission'
import { UserPermission } from '../user/permissions'
import Toast from 'components/atoms/Toast'
import {
  useReverseBillingPaymentMutation,
  useReversePaymentTransactionMutation,
  useUpdatePaymentTransactionMutation,
} from 'store/services/billingPayments'
import {
  aggregateLineTransactions,
  calculateServiceLineBalances,
  hasPayerAdjudication,
} from 'ts-common/utils/serviceLineBalances.utils'
import { PatientWriteOffModal } from './PatientWriteOffModal'
import {
  isWriteOffReason,
  OTHER_WRITE_OFF_REASON,
  writeOffNeedsReason,
} from 'ts-common/utils/writeOffReasons.utils'
import { formatCalendarDate } from 'helpers/dates'
import {
  canEditPaymentDetails,
  changedPaymentDetailsBody,
  PaymentDetailsDraft,
  paymentDetailsDraftOf,
  validatePaymentDetailsDraft,
} from './PaymentMethodDetailsFields'

// One shared grid for the procedure header, the procedure rows and the
// transaction rows nested under them: a chevron gutter, twelve data columns
// (CPT, St. DOS, Charged, Balance, Exp. Allowed, Allowed, Paid/Refund,
// Transfer, Patient Resp., Write-off, Plan Bal., Patient Bal.) and a fixed
// trailing actions column (Apply Payment on the line, Save/Cancel below).
export const serviceLineGrid = {
  display: 'grid',
  gridTemplateColumns: '50px repeat(12, minmax(0, 1fr)) 120px',
  columnGap: '0.5rem',
  alignItems: 'center',
} as const

interface Props {
  serviceLine: ChargeAccountServiceLine
  patientId: number
  chargeId: string
  // Absent for self-pay charges — patient money is never posted payer-style,
  // so those lines carry no Apply Payment action.
  payerChain?: ChargePayerChain | null
  cptDescription?: string
  refetchPayments: () => void
  onOpenTransactionNotes: (transaction: ChargeAccountTransaction) => void
  onOpenApplicationNotes: (application: ChargeAccountLineApplication) => void
  onReturnToUnapplied: (application: ChargeAccountLineApplication) => void
  // A simple receipt for the patient payment behind an applied credit.
  onPrepaymentReceipt?: (prepaymentId: string) => void
  // Present when the charge has more lines than this one: the inline row
  // offers to post one payment for the whole charge instead.
  onPostForCharge?: (payerId: number | null) => void
}

// Dates of service, in every shape they arrive (YYYYMMDD from an ERA, ISO from a charge).
export const formatYyyyMmDd = (v?: string | null) => formatCalendarDate(v, '--')

const formatMoney = (value: number | undefined | null) => {
  return `$${Number(value || 0).toFixed(2)}`
}

// Payments 3.2: an adjudicated allowed that strays from the contracted rate
// beyond this is flagged on the row — 5% of the expected amount, with a $1
// floor so cent-rounding on small contracted rates doesn't light up.
const ALLOWED_VARIANCE_RATIO = 0.05
const ALLOWED_VARIANCE_FLOOR = 1

export const ServiceLineRow: React.FC<Props> = ({
  serviceLine,
  patientId,
  chargeId,
  payerChain,
  cptDescription,
  refetchPayments,
  onOpenTransactionNotes,
  onOpenApplicationNotes,
  onReturnToUnapplied,
  onPrepaymentReceipt,
  onPostForCharge,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isApplying, setIsApplying] = useState(false)
  const [isWritingOff, setIsWritingOff] = useState(false)
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [transactionEdits, setTransactionEdits] = useState<Record<string, Partial<ChargeAccountTransaction> & Record<string, unknown>>>({})
  const [updateTransaction, { isLoading: isSaving }] = useUpdatePaymentTransactionMutation()
  const [reverseTransaction, { isLoading: isReversingTransaction }] = useReversePaymentTransactionMutation()
  const [reversePayment, { isLoading: isReversingPayment }] = useReverseBillingPaymentMutation()
  const isReversing = isReversingTransaction || isReversingPayment
  const [reversingKey, setReversingKey] = useState<string | null>(null)
  const [writeOffReasonErrorKey, setWriteOffReasonErrorKey] = useState<string | null>(null)
  const [paymentDetailsDraft, setPaymentDetailsDraft] = useState<PaymentDetailsDraft | null>(null)

  // Lines merge transactions from several payments, so the payment-relative
  // positional address is gone — a transaction is editable only through its
  // own id (every persisted transaction has one).
  const keyOf = (tx: ChargeAccountTransaction, txIndex: number) => tx.id ?? `pos:${txIndex}`

  const startEditing = (key: string, transaction: ChargeAccountTransaction) => {
    setTransactionEdits(prev => ({
      ...prev,
      [key]: { ...transaction }
    }))
    setPaymentDetailsDraft(
      canEditPaymentDetails(transaction) && transaction.paymentDetails
        ? paymentDetailsDraftOf(transaction.paymentDetails)
        : null,
    )
    setEditingKey(key)
  }

  const cancelEditing = () => {
    setWriteOffReasonErrorKey(null)

    if (editingKey !== null) {
      const newEdits = { ...transactionEdits }
      delete newEdits[editingKey]
      setTransactionEdits(newEdits)
    }
    setPaymentDetailsDraft(null)
    setEditingKey(null)
  }

  const saveTransaction = async (key: string, tx: ChargeAccountTransaction) => {
    const updatedTransaction = transactionEdits[key]
    if (!updatedTransaction || !tx.id) return

    const allowed = Number((updatedTransaction.allowed as number | string) || 0)
    const paidRefund = Number((updatedTransaction.paidRefund as number | string) || 0)
    const transfers = Number((updatedTransaction.transfers as number | string) || 0)
    const copay = Number((updatedTransaction.copay as number | string) || 0)
    const writeOff = Number((updatedTransaction.writeOff as number | string) || 0)
    const writeOffReason = (updatedTransaction.writeOffReason as string | null) ?? null
    const writeOffNote = String((updatedTransaction.writeOffNote as string | undefined) ?? '')

    // A write-off reduces what anyone can still collect, so it does not go in
    // unexplained. Highlighted on the row rather than only toasted, so the
    // biller is pointed at the field that is missing. The server enforces the
    // same rule. A row that already said "Other" had its explanation lifted
    // into a billing note when it was posted, so only a reason newly set to
    // "Other" still owes one.
    const noteOnRecord = tx.writeOffReason === OTHER_WRITE_OFF_REASON
    const reasonMissing = writeOffNeedsReason(writeOff)
      && (!isWriteOffReason(writeOffReason)
        || (writeOffReason === OTHER_WRITE_OFF_REASON && !noteOnRecord && !writeOffNote.trim()))

    if (reasonMissing) {
      setWriteOffReasonErrorKey(key)
      Toast({
        type: 'error',
        label: writeOffReason === OTHER_WRITE_OFF_REASON
          ? 'Write-off reason "Other" needs a short explanation'
          : 'A write-off reason is required',
      })
      return
    }

    setWriteOffReasonErrorKey(null)

    const paymentDetails = paymentDetailsDraft && tx.paymentDetails
      ? changedPaymentDetailsBody(paymentDetailsDraft, paymentDetailsDraftOf(tx.paymentDetails))
      : undefined

    if (paymentDetails && paymentDetailsDraft) {
      const detailsError = validatePaymentDetailsDraft(paymentDetailsDraft)

      if (detailsError) {
        Toast({ type: 'error', label: detailsError })
        return
      }
    }

    try {
      await updateTransaction({
        paymentId: tx.paymentId,
        patientId,
        transactionId: tx.id,
        allowed: Number(allowed.toFixed(2)),
        paidRefund: Number(paidRefund.toFixed(2)),
        transfers: Number(transfers.toFixed(2)),
        copay: Number(copay.toFixed(2)),
        writeOff: Number(writeOff.toFixed(2)),
        writeOffReason: writeOffNeedsReason(writeOff) ? writeOffReason : null,
        ...(writeOffNote.trim() ? { writeOffNote: writeOffNote.trim() } : {}),
        ...(paymentDetails ? { paymentDetails } : {}),
      }).unwrap()

      setEditingKey(null)
      setPaymentDetailsDraft(null)

      const newEdits = { ...transactionEdits }
      delete newEdits[key]

      setTransactionEdits(newEdits)
      refetchPayments()

      Toast({ type: 'success', label: 'Transaction updated successfully' })
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message || 'Failed to save transaction'
      Toast({ type: 'error', label: errorMessage })
    }
  }

  // What an ERA claim posted goes back as its whole payment: every service
  // line, and any patient write-off entered against it, so its claim returns
  // to the ERA pages to be matched and posted again, or archived.
  const reverseWholePayment = async (key: string, tx: ChargeAccountTransaction) => {
    try {
      await confirm('', {
        title: 'Reverse this payment?',
        description:
          `This reverses the whole ${tx.paymentBy || 'payer'} payment${tx.sourceLabel ? ` (${tx.sourceLabel})` : ''}` +
          ` — every service line it posted, not just this one — along with any patient write-offs entered against it.` +
          ` Its ERA claim goes back to pending on the ERA pages, to be matched and posted again or archived,` +
          ` unless a denial it resolved reopens, which keeps the claim.` +
          ` The original entries stay visible. A reversal cannot be reversed.`,
        yesAction: 'Reverse Payment',
      })
    } catch {
      return
    }

    setReversingKey(key)

    try {
      await reversePayment({ paymentId: tx.paymentId, patientId }).unwrap()
      refetchPayments()
      Toast({ type: 'success', label: 'Payment reversed' })
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message || 'Failed to reverse payment'
      Toast({ type: 'error', label: errorMessage })
    } finally {
      setReversingKey(null)
    }
  }

  const reverse = async (key: string, tx: ChargeAccountTransaction) => {
    if (!tx.id) return

    if (reversesWholePayment(tx)) {
      await reverseWholePayment(key, tx)
      return
    }

    // A reversal writes a permanent contra-entry and cannot itself be
    // reversed — never fire it off a stray click.
    try {
      await confirm('', tx.type === 'PATIENT_WRITE_OFF'
        ? {
          title: 'Reverse this patient write-off?',
          description:
            `This restores $${Number(tx.patientWriteOff ?? 0).toFixed(2)} to the patient balance` +
            ` — the forgiveness is backed out and the patient owes the amount again.` +
            ` The original entry stays visible. A reversal cannot be reversed.`,
          yesAction: 'Reverse',
        }
        : {
          title: 'Reverse this transaction?',
          description:
            `This adds an opposite-sign entry backing out the full amounts` +
            ` (paid/refund $${Number(tx.paidRefund ?? 0).toFixed(2)}) posted by ${tx.paymentBy || 'this payer'}.` +
            ` The original entry stays visible. A reversal cannot be reversed.`,
          yesAction: 'Reverse',
        })
    } catch {
      return
    }

    setReversingKey(key)

    try {
      await reverseTransaction({ paymentId: tx.paymentId, patientId, transactionId: tx.id }).unwrap()
      refetchPayments()
      Toast({ type: 'success', label: 'Transaction reversed' })
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message || 'Failed to reverse transaction'
      Toast({ type: 'error', label: errorMessage })
    } finally {
      setReversingKey(null)
    }
  }

  const updateField = (key: string, field: keyof ChargeAccountTransaction, value: number | string) => {
    setTransactionEdits(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }))
  }

  const getCurrentTransaction = (key: string, original: ChargeAccountTransaction): ChargeAccountTransaction => {
    return (transactionEdits[key] as ChargeAccountTransaction) || original
  }

  const mergedTransactions: ChargeAccountTransaction[] = (serviceLine.transactions || []).map((tx, txIndex) => {
    const key = keyOf(tx, txIndex)
    return editingKey === key && transactionEdits[key] ? { ...tx, ...transactionEdits[key] } : tx
  })

  const hasTransactions = mergedTransactions.length > 0
  const patientApplications = serviceLine.patientApplications || []
  // A self-pay line settled from credit carries no payer transaction, so the
  // line still has to open.
  const hasActivity = hasTransactions || patientApplications.length > 0
  const appliedPatientAmount = Number(
    patientApplications
      .reduce((total, application) => total + (Number(application.appliedAmount) || 0), 0)
      .toFixed(2),
  )

  // Payer-style posting needs a payer chain to post from. Unmatched lines
  // take postings too — the payment lands on their CPT and keeps counting
  // toward the charge like any other.
  const canPostInsurancePayments = useLabPermission(UserPermission.POST_INSURANCE_PAYMENTS)
  const canApplyPayment = Boolean(payerChain?.primaryPayerId) && canPostInsurancePayments
  const canWriteOffPatientBalance = useLabPermission(UserPermission.WRITE_OFF_PATIENT_BALANCE)

  // The line's payers read together, the way the charge rollup reads them:
  // allowed is the first payer's, transfers are the deepest payer's, the rest
  // adds up. Summing every payer's allowed would count a secondary's
  // adjudication of the transfer as more allowed, and leave the transfer it
  // settled as a phantom plan balance.
  const totals = aggregateLineTransactions(mergedTransactions)

  // A line whose only rows are patient write-offs (and their reversals) has
  // NOT been adjudicated: its patient base is still the charge's own patient
  // amount, and its payer-side columns must not switch to (empty) payer sums.
  const payerAdjudicated = hasPayerAdjudication(mergedTransactions)

  // A $0-paid line carrying payer-side denial codes is a denial; a line with
  // no transactions at all just hasn't been adjudicated yet.
  const isDenied =
    totals.paidRefund === 0 &&
    mergedTransactions.some((tx) => isDeniedAdjudication(tx.paidRefund, tx.adjustments))

  // Untouched lines show the charge's own split; once a payer posts, the
  // shared balance formula takes over — same math as everywhere else.
  const charged = Number(serviceLine.charged ?? 0) || 0
  const calculated = calculateServiceLineBalances({ charged, ...totals })
  const planBalance = payerAdjudicated ? calculated.planBalance : Number(serviceLine.planAmount ?? 0) || 0
  // Patient Resp. is what the payer stated, or what the charge bills the
  // patient on a line no payer has adjudicated — gross, so the payer's word
  // stays readable after a forgiveness.
  const patientResponsibility = payerAdjudicated
    ? Number(totals.copay.toFixed(2))
    : Number(serviceLine.patientAmount ?? 0) || 0
  // The balance is what is still open after the patient's own money settled
  // part of it and after any forgiven responsibility came off.
  const patientBalance = Math.max(
    0,
    Number((patientResponsibility - totals.patientWriteOff - appliedPatientAmount).toFixed(2)),
  )
  // What is still open on the line, plan and patient together: it falls as
  // money posts, and reads zero on a line settled at both levels.
  const balance = Number((planBalance + patientBalance).toFixed(2))

  // Expected-vs-actual variance, only once something has adjudicated — an
  // untouched line has no actual to compare. Both directions are flagged;
  // underpayment is what the biller is hunting for.
  const expectedAllowed = serviceLine.expectedAllowed
  const allowedVariance = expectedAllowed != null && payerAdjudicated
    ? Number((totals.allowed - expectedAllowed).toFixed(2))
    : null
  const varianceThreshold = expectedAllowed != null
    ? Math.max(ALLOWED_VARIANCE_FLOOR, ALLOWED_VARIANCE_RATIO * expectedAllowed)
    : 0
  const hasAllowedVariance = allowedVariance !== null && Math.abs(allowedVariance) > varianceThreshold

  return (
    <div>
      <div
        css={{
          ...serviceLineGrid,
          padding: '0.75rem 0',
          borderBottom: '1px solid #dee2e6',
          cursor: hasActivity ? 'pointer' : 'default',
          '&:hover': hasActivity ? { backgroundColor: '#e9ecef' } : undefined,
        }}
        onClick={() => hasActivity && setIsExpanded(!isExpanded)}>
        <div css={{ paddingLeft: '0.5rem' }}>
          {hasActivity
            ? <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} css={{ color: '#495057' }} />
            : null}
        </div>
        <div>
          <TooltipWrapper text={cptDescription || ''} show={Boolean(cptDescription)} maxWidth={440}>
            <span
              css={cptDescription
                ? { cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 2 }
                : undefined}>
              {serviceLine.cptCode || '--'}
            </span>
          </TooltipWrapper>
          {serviceLine.unmatched && (
            <TooltipWrapper
              text="This payment could not be matched to a procedure on the charge. Its amounts still count toward the charge balances."
              show
              maxWidth={320}>
              <i className="fas fa-exclamation-circle" css={{ color: 'red', marginLeft: 6, cursor: 'help' }} />
            </TooltipWrapper>
          )}
          {isDenied && (
            <span css={{ marginLeft: 5 }}>
              <DeniedBadge />
            </span>
          )}
        </div>
        <div>{formatYyyyMmDd(serviceLine.startDos)}</div>
        <div>{formatMoney(charged)}</div>
        <div>{formatMoney(balance)}</div>
        <div>{serviceLine.expectedAllowed != null ? formatMoney(serviceLine.expectedAllowed) : '--'}</div>
        <div>
          {hasAllowedVariance ? (
            <TooltipWrapper
              text={`Allowed ${formatMoney(totals.allowed)} vs expected ${formatMoney(expectedAllowed)} — `
                + `${(allowedVariance ?? 0) < 0 ? 'under' : 'over'} by ${formatMoney(Math.abs(allowedVariance ?? 0))}`}
              show
              maxWidth={320}>
              <span
                css={{
                  backgroundColor: '#fde0e0',
                  color: '#dc3545',
                  fontWeight: 600,
                  borderRadius: '4px',
                  padding: '0.1rem 0.4rem',
                  cursor: 'help',
                }}>
                {formatMoney(totals.allowed)}
              </span>
            </TooltipWrapper>
          ) : (
            formatMoney(totals.allowed)
          )}
        </div>
        <div>{formatMoney(totals.paidRefund)}</div>
        <div>{formatMoney(totals.transfers)}</div>
        <div>{formatMoney(patientResponsibility)}</div>
        <div>{formatMoney(totals.writeOff)}</div>
        <div>{formatMoney(planBalance)}</div>
        <div>{formatMoney(patientBalance)}</div>
        {/* The actions column is 120px: the two buttons stack. */}
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '0.25rem',
            paddingRight: '0.5rem',
          }}>
          {canApplyPayment && !isApplying && (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              css={{ whiteSpace: 'nowrap', padding: '0.25rem 0.5rem', fontSize: '0.813rem' }}
              onClick={(event) => {
                event.stopPropagation()
                setIsApplying(true)
              }}>
              Apply Payment
            </button>
          )}
          {canWriteOffPatientBalance && patientBalance > 0 && (
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              title="Forgive some or all of what the patient owes on this line — no money moves"
              css={{ whiteSpace: 'nowrap', padding: '0.25rem 0.5rem', fontSize: '0.813rem' }}
              onClick={(event) => {
                event.stopPropagation()
                setIsWritingOff(true)
              }}>
              Write Off
            </button>
          )}
        </div>
      </div>

      {isWritingOff && (
        <PatientWriteOffModal
          patientId={patientId}
          chargeId={chargeId}
          cptCode={serviceLine.cptCode}
          startDos={serviceLine.startDos}
          patientBalance={patientBalance}
          onClose={() => setIsWritingOff(false)}
          onDone={refetchPayments}
        />
      )}

      {isApplying && payerChain && (
        <ApplyPaymentRow
          patientId={patientId}
          chargeId={chargeId}
          serviceLine={serviceLine}
          payerChain={payerChain}
          onClose={() => setIsApplying(false)}
          onDone={refetchPayments}
          onPostForCharge={onPostForCharge}
        />
      )}

      {isExpanded && hasActivity && (
        <div>
          {/* One history, oldest first: postings, reversals, write-offs and the
              patient's applied credit in the order they happened. */}
          {lineActivity(serviceLine.transactions, patientApplications).map((entry) => {
            if (entry.kind === 'application') {
              const { application, index: applicationIndex } = entry

              return (
                <PatientApplicationRow
                  key={`${application.applicationId}-${applicationIndex}`}
                  application={application}
                  onOpenNotes={() => onOpenApplicationNotes(application)}
                  onReturnToUnapplied={() => onReturnToUnapplied(application)}
                  onReceipt={onPrepaymentReceipt}
                />
              )
            }

            const { transaction: tx, index: txIndex } = entry
            const key = keyOf(tx, txIndex)
            const isEditing = editingKey === key
            const currentTx = isEditing ? getCurrentTransaction(key, tx) : tx

            return (
              <TransactionRow
                key={key}
                transaction={currentTx}
                patientId={patientId}
                isEditing={isEditing}
                isSaving={isSaving && isEditing}
                isReversing={isReversing && reversingKey === key}
                isFullyReversed={isFullyReversed(tx, serviceLine.transactions)}
                onEdit={() => startEditing(key, tx)}
                onSave={() => saveTransaction(key, tx)}
                onCancel={cancelEditing}
                onReverse={() => reverse(key, tx)}
                onFieldChange={(field, value) => updateField(key, field, value)}
                writeOffReasonError={writeOffReasonErrorKey === key}
                writeOffNoteOnRecord={tx.writeOffReason === OTHER_WRITE_OFF_REASON}
                onOpenNotes={() => onOpenTransactionNotes(tx)}
                onAttachmentsChange={refetchPayments}
                paymentDetails={isEditing ? paymentDetailsDraft : null}
                onPaymentDetailsChange={setPaymentDetailsDraft}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}
